import type React from "react";

import { useDispatch, useSelector } from "react-redux";

import { IconButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import useStaticListSizeQuota from "app/pages/sales-intelligence/hooks/useStaticListSizeQuota";
import useTargetAccountsTrackingService from "app/pages/sales-intelligence/hooks/useTargetAccountsTrackingService";
import {
    StyledHeaderListContainer,
    StyledSearchTextBox,
} from "app/pages/sales-intelligence/modules/lists/containers/ListTabsContainer/styles";
import listTrackingService from "app/pages/sales-intelligence/modules/lists/services/tracking/trackingService";
import QuotaContainer from "app/pages/sales-intelligence/modules/quota/containers/QuotaContainer/QuotaContainer";
import { toggleOpportunityListModal } from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import { selectAllUniqueWebsites } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import { openHooksModalV5 } from "app/services/ModalService";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

type StaticListHeaderContainerProps = {
    searchText: string;
    setSearchText: (searchText: string) => void;
};

const StaticListHeaderContainer: React.FC<StaticListHeaderContainerProps> = ({ searchText, setSearchText }) => {
    const dispatch = useDispatch();
    const allUniqueOpportunities = useSelector(selectAllUniqueWebsites);
    const trackingService = useTargetAccountsTrackingService();

    const { isListSizeQuotaReached } = useStaticListSizeQuota();

    const onSearch = (text: string) => {
        setSearchText(text);
    };

    const onSearchClear = () => {
        setSearchText("");
    };

    const handleAddButtonClick = () => {
        trackingService.trackAddListClicked(allUniqueOpportunities.length);
        dispatch(toggleOpportunityListModal(true));
    };

    const handleHookClick = () => {
        listTrackingService.trackCreateNewListHookClick();
        openHooksModalV5("SalesSolutionLists");
    };

    return (
        <StyledHeaderListContainer>
            <StyledSearchTextBox
                onChange={onSearch}
                onClear={onSearchClear}
                searchText={searchText}
                placeholder={translate("si.components.list.search_placeholder")}
            />
            <FlexRow alignItems="center">
                <QuotaContainer />
                {isListSizeQuotaReached ? (
                    <IconButton
                        type="trial"
                        iconName="sparkles"
                        iconSize="xs"
                        onClick={handleHookClick}
                    >
                        {translate("si.my-list.static-list.upgrade.button")}
                    </IconButton>
                ) : (
                    <IconButton
                        type="primary"
                        iconName="plus"
                        iconSize="xs"
                        onClick={handleAddButtonClick}
                    >
                        {translate("si.my-list.static-list.button.add-new-list")}
                    </IconButton>
                )}
            </FlexRow>
        </StyledHeaderListContainer>
    );
};

export default StaticListHeaderContainer;
